import { useEffect, useState, useRef } from "react";
import { obtenerCentro } from "../services/centroService";
import { obtenerAmbitoAprendizaje, registrarEvaluaciones, registrarReporteProgreso, registrarReporteResumen } from "../services/evaluacionesService";
import { formatearFechaLocal } from '../utils/dateUtils';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import es from 'date-fns/locale/es';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ChevronRight, ChevronLeft, Check } from 'lucide-react';

const useResizeHandler = () => {
  const [isResizing, setIsResizing] = useState(false);
  const resizeTimeoutRef = useRef(null);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (!isResizing) {
        setIsResizing(true);
      }
      
      // Limpiar el timeout anterior si existe
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      // Establecer un nuevo timeout
      resizeTimeoutRef.current = setTimeout(() => {
        setIsResizing(false);
      }, 100);
    });

    const elements = document.querySelectorAll('.resize-observe');
    elements.forEach(element => {
      resizeObserver.observe(element);
    });

    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeObserver.disconnect();
    };
  }, []);

  return isResizing;
};

const PlantillaEvaluacion = ({ grupoEdad, nombre, apellidos, fechaNacimiento, idChild }) => {
  const [centro, setCentro] = useState(null);
  const [ambitos, setAmbitos] = useState([]);
  // Inicializar fecha con la fecha actual en zona horaria de Ecuador
  const [fecha, setFecha] = useState(() => {
    const fechaActual = new Date();
    return new Date(fechaActual.toLocaleString('en-US', { timeZone: 'America/Guayaquil' }));
  });
  const [seleccionados, setSeleccionados] = useState({});
  const [observaciones, setObservaciones] = useState('');
  const [faseActual, setFaseActual] = useState(0);
  const [ambitosPorFase, setAmbitosPorFase] = useState([]);
  const isResizing = useResizeHandler();

  // Orden predeterminado de los ámbitos de aprendizaje por grupo de edad
  const ambitosOrdenPorGrupo = {
    "3-6 meses": [
      "Vinculación emocional y social",
      "Exploración del cuerpo y motricidad",
      "Descubrimiento del medio natural y cultural", 
      "Lenguaje verbal y no verbal"
    ],
    "6-9 meses": [
      "Vinculación emocional y social",
      "Exploración del cuerpo y motricidad",
      "Descubrimiento del medio natural y cultural", 
      "Lenguaje verbal y no verbal"
    ],
    "9-12 meses": [
      "Vinculación afectiva y comunicación",
      "Coordinación sensorio-motriz",
      "Descubrimiento del entorno",
      "Lenguaje expresivo y comprensivo"
    ],
    "12-24 meses": [
      "Identidad y autonomía",
      "Expresión corporal y motricidad", 
      "Descubrimiento y comprensión del medio",
      "Lenguaje verbal y no verbal"
    ],
    "24-36 meses": [
      "Identidad y autonomía",
      "Expresión corporal y motricidad", 
      "Descubrimiento y comprensión del medio",
      "Lenguaje verbal y no verbal"
    ],
    "36-48 meses": [
      "Identidad y autonomía",
      "Expresión corporal y motricidad", 
      "Descubrimiento y comprensión del medio",
      "Lenguaje verbal y no verbal"
    ]
  };

  // Niveles de logro con etiquetas descriptivas y colores mejorados
  const nivelesLogro = [
    {
      valor: 1,
      etiqueta: 'No lo consigue',
      color: 'bg-red-100 hover:bg-red-200 border-red-300',
      textColor: 'text-red-800'
    },
    {
      valor: 2,
      etiqueta: 'En proceso',
      color: 'bg-yellow-100 hover:bg-yellow-200 border-yellow-300',
      textColor: 'text-yellow-800'
    },
    {
      valor: 3,
      etiqueta: 'Domina el logro',
      color: 'bg-green-100 hover:bg-green-200 border-green-300',
      textColor: 'text-green-800'
    }
  ];

  // Verificar si todos los campos del ámbito actual están completos
  const ambitoActualCompleto = () => {
    if (!ambitosPorFase[faseActual]) return false;
    return ambitosPorFase[faseActual].every(ambito => 
      seleccionados[ambito.id_amb_aprendizaje] !== undefined
    );
  };

  // Verificar si todos los campos requeridos están completos
  const todosLosCamposCompletos = () => {
    if (!nombre || !apellidos || !fechaNacimiento) return false;
    if (!centro?.nombre_unidad || !centro?.zona || !centro?.provincia || !centro?.canton) return false;
    if (!ambitos || ambitos.length === 0) return false;
    return ambitos.every(ambito => seleccionados[ambito.id_amb_aprendizaje] !== undefined);
  };

  // Función para avanzar a la siguiente fase
  const siguienteFase = () => {
    if (faseActual < ambitosPorFase.length - 1) {
      setFaseActual(prev => prev + 1);
    }
  };

  // Función para retroceder a la fase anterior
  const faseAnterior = () => {
    if (faseActual > 0) {
      setFaseActual(prev => prev - 1);
    }
  };

  // Calcular el progreso total
  const calcularProgreso = () => {
    const totalAmbitos = ambitos.length;
    const ambitosCompletados = Object.keys(seleccionados).length;
    return (ambitosCompletados / totalAmbitos) * 100;
  };

  // Función para limpiar el formulario
  const limpiarFormulario = () => {
    setSeleccionados({});
    setObservaciones('');
    // Reiniciar fecha con la fecha actual en zona horaria de Ecuador
    const fechaActual = new Date();
    setFecha(new Date(fechaActual.toLocaleString('en-US', { timeZone: 'America/Guayaquil' })));
    setFaseActual(0);
  };



  // Manejar el registro de la evaluación
  const handleRegistrarEvaluacion = async () => {
    try {
      if (!todosLosCamposCompletos()) {
        toast.warn('Debe seleccionar al menos un nivel de logro para cada ámbito de aprendizaje', {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          className: 'z-50'
        });
        return;
      }
  
      // Mostrar toast de carga
      toast.info('Registrando evaluación...', {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        className: 'z-50'
      });
  
      // Preparar los datos para enviar
      const evaluacionData = {
        datosChild: {
          id_child: idChild
        },
        datosCentro: {
          id_centro: centro.id_centro
        },
        datosEvaluacion: {
          fecha: formatearFechaLocal(fecha)
        },
        ambitos: Object.entries(seleccionados).map(([id_ambito, nivel_logro]) => ({
          id_ambito,
          nivel_logro
        }))
      };
  
      const response = await registrarEvaluaciones(evaluacionData);
      console.log("Respuesta del reporte de progreso:", response);
      if (response.success) {
        // Obtener los ámbitos únicos
        const ambitosUnicos = [...new Set(response.data.evaluacion.ambitos.map(a => a.ambito_aprendizaje))];
        
        // Calcular porcentaje para cada ámbito
        const calcularPorcentajeAmbito = (ambitos, nombreAmbito) => {
          const ambitosIguales = ambitos.filter(a => a.ambito_aprendizaje === nombreAmbito);
          const sumaNivelesLogro = ambitosIguales.reduce((sum, a) => sum + parseInt(a.nivel_logro), 0);
          const divisor = ambitosIguales.length * 3;
          return (sumaNivelesLogro / divisor) * 100;
        };

        const ambitosConPorcentajes = ambitosUnicos.map(ambito => {
          const porcentaje = calcularPorcentajeAmbito(response.data.evaluacion.ambitos, ambito);
          return {
            ambito: ambito,
            porcentaje: parseFloat(porcentaje.toFixed(2))
          };
        });

        // Preparar datos para el reporte de progreso
        const datosReporteProgreso = {
          datosReporteProgreso: {
            id_evaluacion: response.data.evaluacion.id_evaluacion,
            ambitos: ambitosConPorcentajes
          }
        };

        const resProgreso = await registrarReporteProgreso(datosReporteProgreso);
        console.log("Respuesta del reporte de progreso:", resProgreso);
        
        if (resProgreso.success) {
          // Función auxiliar para determinar el logro equivalente
          const determinarLogroEquivalente = (porcentaje) => {
            if (porcentaje <= 66) return "NO LO CONSIGUE";
            if (porcentaje <= 86) return "EN PROCESO"; 
            if (porcentaje < 101) return "DOMINA EL LOGRO";
            return "DOMINA EL LOGRO";
          };

          // Calcular el porcentaje total y nivel de logro
          const sumaNivelesLogro = response.data.evaluacion.ambitos.reduce((sum, a) => sum + parseInt(a.nivel_logro), 0);
          console.log("Cantidad de ámbitos evaluados:", response.data.evaluacion.ambitos.length);
          const cantidadTotalAmbitos = response.data.evaluacion.ambitos.length * 3;
          const porcentajeTotal = parseFloat(((sumaNivelesLogro / cantidadTotalAmbitos) * 100).toFixed(2));

          // Preparar datos para el reporte resumen
          const datosReporteResumen = {
            datosResporteResumen: {
              id_evaluacion: response.data.evaluacion.id_evaluacion,
              porcentaje_total: porcentajeTotal,
              logro_equivalente: determinarLogroEquivalente(porcentajeTotal)
            }
          };

          // Registrar reporte resumen
          const resResumen = await registrarReporteResumen(datosReporteResumen);
          console.log("Respuesta del reporte resumen:", resResumen);

          if (resResumen.success) {
            toast.success('¡Evaluación y reportes registrados exitosamente!', {
              position: "top-right",
              autoClose: 4000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              className: 'z-50'
            });
            limpiarFormulario();
          } else {
            throw new Error('Error al registrar el reporte resumen');
          }
        } else {
          throw new Error('Error al registrar el reporte de progreso');
        }
      } else {
        throw new Error('Error al registrar la evaluación');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al registrar. Por favor, inténtelo de nuevo.', {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        className: 'z-50'
      });
    }
  };

  // Obtener datos del centro
  useEffect(() => {
    const fetchCentro = async () => {
      try {
        const response = await obtenerCentro();
        
        if (response.success) {
          const centroData = Array.isArray(response.data)
            ? response.data[0]
            : response.data;
            
          console.log("Datos del centro obtenidos:");
          
          setCentro(centroData);
        } else {
          console.error("No se pudieron obtener los datos del centro");
        }
      } catch (error) {
        console.error("Error al obtener datos del centro:", error);
      }
    };
    
    fetchCentro();
  }, []);

  // Obtener ámbitos de aprendizaje según el grupo de edad y organizarlos por fases
  useEffect(() => {
    const fetchAmbitos = async () => {
      try {
        const dataAmbitos = await obtenerAmbitoAprendizaje();
        const ambitosFiltrados = dataAmbitos.data.filter(
          ambito => ambito.grupo_edad === grupoEdad
        );
        
        // Ordenar los ámbitos según el orden predeterminado
        const ambitosOrdenados = ambitosFiltrados.sort((a, b) =>
          ambitosOrdenPorGrupo[grupoEdad].indexOf(a.ambito_aprendizaje) -
          ambitosOrdenPorGrupo[grupoEdad].indexOf(b.ambito_aprendizaje)
        );
        
        setAmbitos(ambitosOrdenados);

        // Agrupar ámbitos por tipo para las fases
        const ambitosAgrupados = ambitosOrdenados.reduce((acc, ambito) => {
          const tipo = ambito.ambito_aprendizaje;
          if (!acc[tipo]) {
            acc[tipo] = [];
          }
          acc[tipo].push(ambito);
          return acc;
        }, {});

        setAmbitosPorFase(Object.values(ambitosAgrupados));
      } catch (error) {
        console.error("Error al obtener ámbitos de aprendizaje:", error);
      }
    };
    
    if (grupoEdad) {
      fetchAmbitos();
    }
  }, [grupoEdad]);

  // Manejar selección de logros
  const handleLogroChange = (ambitoId, valor) => {
    setSeleccionados(prev => ({
      ...prev,
      [ambitoId]: valor
    }));
  };

  if (!grupoEdad) return <p className="text-gray-500">Seleccione un grupo de edad</p>;
  if (!centro) return <p className="text-gray-500">Cargando datos del centro...</p>;
  if (ambitos.length === 0) return <p className="text-gray-500">No hay ámbitos para este grupo de edad</p>;

  return (
    <div className="plantilla-evaluacion bg-gradient-to-br from-blue-50 to-blue-100 p-2 sm:p-4 rounded-xl shadow-lg max-w-6xl mx-auto relative overflow-hidden">
      {/* Fondo decorativo */}
      <div className="absolute top-0 left-0 right-0 bottom-0 opacity-10">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="w-full">
          <path fill="#3B82F6" fillOpacity="1" d="M0,160L48,176C96,192,192,224,288,245.3C384,267,480,277,576,261.3C672,245,768,203,864,176C960,149,1056,139,1152,154.7C1248,171,1344,213,1392,234.7L1440,256L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
        </svg>
      </div>

      {/* Contenido principal */}
      <div className="relative z-10">
        {/* Encabezado */}
        <div className="text-center mb-4">
          <h2 className="text-xl sm:text-2xl font-bold text-blue-900 mb-2">
            FICHA DE EVALUACIÓN
          </h2>
        </div>

        {/* Datos del centro */}
        <div className="bg-white/80 backdrop-blur-sm p-3 sm:p-4 rounded-xl mb-4 grid grid-cols-1 md:grid-cols-2 gap-3 shadow-md">
          <div className="col-span-1 md:col-span-2 border-b pb-2">
            <h3 className="text-base sm:text-lg font-semibold text-blue-800">
              CENTRO DE DESARROLLO INFANTIL
            </h3>
            <p className="text-xs sm:text-sm text-gray-700">{centro.nombre_unidad || centro.nombre || 'No disponible'}</p>
          </div>
          
          <div className="space-y-1 text-xs sm:text-sm">
            <p className="font-medium text-gray-600">
              <strong className="text-blue-800">ZONA:</strong> {centro.zona || 'No disponible'}
            </p>
            <p className="font-medium text-gray-600">
              <strong className="text-blue-800">PROVINCIA:</strong> {centro.provincia || 'No disponible'}
            </p>
            <p className="font-medium text-gray-600">
              <strong className="text-blue-800">CANTÓN:</strong> {centro.canton || 'No disponible'}
            </p>
          </div>
          
          <div className="space-y-1 text-xs sm:text-sm">
            <p className="font-medium text-gray-600">
              <strong className="text-blue-800">PARROQUIA:</strong> {centro.parroquia || 'No disponible'}
            </p>
            <p className="font-medium text-gray-600">
              <strong className="text-blue-800">DISTRITO:</strong> {centro.distrito || 'No disponible'}
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <label className="text-blue-800 font-semibold whitespace-nowrap">
                FECHA DE EVALUACION
              </label>
              <DatePicker
                selected={fecha}
                onChange={(date) => {
                  // Convertir la fecha seleccionada a zona horaria de Ecuador
                  const fechaEcuador = new Date(date.toLocaleString('en-US', { timeZone: 'America/Guayaquil' }));
                  setFecha(fechaEcuador);
                }}
                dateFormat="dd/MM/yyyy"
                locale={es}
                popperClassName="z-[999]"
                className="w-full sm:w-auto border border-blue-300 rounded-md px-2 py-1
                          focus:ring-1 focus:ring-blue-500 focus:border-transparent 
                          transition duration-300 ease-in-out bg-white text-black text-xs sm:text-sm"
                popperPlacement="bottom-start"
                // Limitar la selección de fechas hasta hoy (zona horaria de Ecuador)
                maxDate={new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Guayaquil' }))}
              />
            </div>
          </div>
        </div>

        {/* Información del niño */}
        <div className="bg-white/80 backdrop-blur-sm p-3 sm:p-4 rounded-xl mb-4 grid grid-cols-1 md:grid-cols-2 gap-3 shadow-md">
          <div className="relative">
            <label className="block mb-1 text-blue-800 font-semibold text-xs sm:text-sm">
              NOMBRE DE LA NIÑA/O
            </label>
            <input 
              type="text"
              value={nombre && apellidos ? `${nombre} ${apellidos}` : ''}
              readOnly
              className="w-full border border-blue-300 rounded-md px-2 py-1
                       focus:ring-1 focus:ring-blue-500 focus:border-transparent 
                       transition duration-300 ease-in-out bg-white text-black text-xs sm:text-sm"
              placeholder="Sin datos del niño/a"
            />
          </div>
          <div className="relative">
            <label className="block mb-1 text-blue-800 font-semibold text-xs sm:text-sm">
              FECHA DE NACIMIENTO
            </label>
            <input
              type="text"
              value={fechaNacimiento || ''}
              readOnly
              className="w-full border border-blue-300 rounded-md px-2 py-1
                       focus:ring-1 focus:ring-blue-500 focus:border-transparent 
                       transition duration-300 ease-in-out bg-white text-black text-xs sm:text-sm"
              placeholder="Sin fecha de nacimiento"
            />
          </div>
        </div>

        {/* Barra de progreso */}
        <div className="mb-4 sm:mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs sm:text-sm font-medium text-blue-800">
              Progreso de la evaluación
            </span>
            <span className="text-xs sm:text-sm font-medium text-blue-800">
              {Math.round(calcularProgreso())}%
            </span>
          </div>
          <div className="w-full bg-blue-100 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${calcularProgreso()}%` }}
            ></div>
          </div>
        </div>

        {/* Área de evaluación por fases */}
        <div className="resize-observe bg-white/90 backdrop-blur-xl rounded-xl p-3 sm:p-6 shadow-lg mb-4 sm:mb-6">
          {!isResizing && ambitosPorFase[faseActual] && (
            <>
              <h3 className="text-lg sm:text-xl font-semibold text-blue-900 mb-3 sm:mb-4 flex items-center gap-2">
                <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-blue-600 font-bold text-sm sm:text-base">{faseActual + 1}</span>
                </div>
                <span className="text-sm sm:text-base truncate">{ambitosPorFase[faseActual][0].ambito_aprendizaje}</span>
              </h3>
              
              <div className="space-y-4 sm:space-y-6">
                {ambitosPorFase[faseActual].map((ambito, index) => (
                  <div key={ambito.id_amb_aprendizaje} 
                       className="resize-observe bg-white rounded-lg p-3 sm:p-4 border border-slate-100 hover:border-blue-200 transition-all duration-300">
                    <p className="text-xs sm:text-sm text-gray-700 mb-3 sm:mb-4 sm:min-h-[48px]">{ambito.indicador}</p>
                    <div className="flex flex-col sm:flex-row sm:justify-center gap-2 sm:gap-4">
                      {nivelesLogro.map((nivel) => (
                        <button
                          key={nivel.valor}
                          onClick={() => handleLogroChange(ambito.id_amb_aprendizaje, nivel.valor)}
                          className={`
                            flex items-center justify-between
                            sm:flex-1 sm:flex-col sm:items-center sm:justify-center
                            p-2 sm:p-4 rounded-lg border transition-all duration-300
                            ${seleccionados[ambito.id_amb_aprendizaje] === nivel.valor 
                              ? `${nivel.color} border-2` 
                              : 'bg-gray-50 hover:bg-gray-100 border-gray-200'}
                          `}
                        >
                          <span className={`text-xs sm:text-sm font-medium ${nivel.textColor}`}>
                            {nivel.etiqueta}
                          </span>
                          {seleccionados[ambito.id_amb_aprendizaje] === nivel.valor && (
                            <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 ml-2 sm:ml-0 sm:mt-2" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Navegación entre fases */}
        <div className="flex justify-center gap-2 sm:gap-4 mt-4 sm:mt-6 mb-4 sm:mb-6">
          {faseActual > 0 && (
            <button
              onClick={faseAnterior}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 sm:px-6 py-2 rounded-full flex items-center gap-1 sm:gap-2 transition-all duration-300 text-xs sm:text-sm"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              Anterior
            </button>
          )}
          
          {faseActual < ambitosPorFase.length - 1 && ambitoActualCompleto() && (
            <button
              onClick={siguienteFase}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 sm:px-6 py-2 rounded-full flex items-center gap-1 sm:gap-2 transition-all duration-300 text-xs sm:text-sm"
            >
              Siguiente
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          )}
        </div>

        {/* Sección de observaciones y botón de registro */}
        {faseActual === ambitosPorFase.length - 1 && (
          <>
            <div className="bg-white/90 backdrop-blur-xl rounded-xl p-3 sm:p-6 shadow-lg mb-4 sm:mb-6">
              <label className="block mb-2 text-lg sm:text-xl font-bold text-blue-900">
                OBSERVACIONES
              </label>
              <textarea 
                className="w-full p-2 sm:p-3 border border-blue-300 rounded-lg
                         min-h-[80px] sm:min-h-[100px] focus:ring-2 focus:ring-blue-500 
                         focus:border-transparent transition-all duration-300
                         resize-y bg-white text-black text-xs sm:text-sm"
                placeholder="Ingrese sus observaciones detalladas aquí..."
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                maxLength={200}
              />
            </div>

            <div className="text-center">
              <button
                onClick={handleRegistrarEvaluacion}
                disabled={!todosLosCamposCompletos()}
                className={`
                  px-6 sm:px-8 py-2 sm:py-3 rounded-xl text-white font-bold text-xs sm:text-sm
                  transition-all duration-300 transform hover:scale-105
                  ${todosLosCamposCompletos()
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 shadow-lg hover:shadow-blue-200'
                    : 'bg-gray-400 cursor-not-allowed'}
                `}
              >
                Registrar Evaluación
              </button>
            </div>
          </>
        )}
      </div>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        className="z-50"
      />
    </div>
  );
};

export default PlantillaEvaluacion;